import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Button,
  Chip,
  Divider,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FiberManualRecord as DotIcon,
  Phone as PhoneIcon,
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { EmailAccount, PhoneNumber } from './types';
import { apiService } from '../../services/api';
import EmailAccountForm from './EmailAccountForm';
import PhoneNumberForm from './PhoneNumberForm';
import { timeAgo } from './utils';

interface AccountManagerDrawerProps {
  open: boolean;
  onClose: () => void;
  onAccountsChanged: () => void;
}

const AccountManagerDrawer: React.FC<AccountManagerDrawerProps> = ({
  open,
  onClose,
  onAccountsChanged,
}) => {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(false);

  // Email account form
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);

  // Phone number form
  const [phoneFormOpen, setPhoneFormOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState<PhoneNumber | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [emailResult, phoneResult] = await Promise.all([
        apiService.getEmailSettings(),
        apiService.getPhoneNumbers(),
      ]);
      setAccounts(((emailResult as any).data || []) as EmailAccount[]);
      setPhoneNumbers(((phoneResult as any).data || []) as PhoneNumber[]);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Disconnect this email account?')) return;
    try {
      await apiService.deleteEmailSettings(accountId);
      fetchData();
      onAccountsChanged();
    } catch {
      // Error handled by API
    }
  };

  const handleDeletePhone = async (phoneId: string) => {
    if (!confirm('Remove this phone number?')) return;
    try {
      await apiService.deletePhoneNumber(phoneId);
      fetchData();
      onAccountsChanged();
    } catch {
      // Error handled by API
    }
  };

  const handleAccountSaved = () => {
    fetchData();
    onAccountsChanged();
  };

  const handlePhoneSaved = () => {
    fetchData();
    onAccountsChanged();
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 400 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Manage Accounts</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Email Accounts Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Email Accounts
                </Typography>
                <Chip
                  label={`${accounts.length} of 5 used`}
                  size="small"
                  color={accounts.length >= 5 ? 'error' : 'default'}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>

              {accounts.length === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No email accounts connected. Add one to start receiving messages.
                </Alert>
              ) : (
                <List disablePadding>
                  {accounts.map((account) => (
                    <ListItem
                      key={account.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        pr: 10,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <DotIcon sx={{ fontSize: 16, color: account.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {account.accountName || account.email}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {account.email}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              {account.connectionStatus === 'connected' ? (
                                <ConnectedIcon sx={{ fontSize: 12, color: 'success.main' }} />
                              ) : (
                                <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />
                              )}
                              <Typography variant="caption" color={account.connectionStatus === 'connected' ? 'success.main' : 'error.main'}>
                                {account.connectionStatus === 'connected' ? 'Connected' : 'Error'}
                              </Typography>
                              {account.lastSyncAt && (
                                <Typography variant="caption" color="text.disabled">
                                  — Synced {timeAgo(account.lastSyncAt)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => { setEditingAccount(account); setEmailFormOpen(true); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Disconnect">
                          <IconButton size="small" onClick={() => handleDeleteAccount(account.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => { setEditingAccount(null); setEmailFormOpen(true); }}
                disabled={accounts.length >= 5}
                fullWidth
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Add Email Account
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Phone Numbers Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Phone Numbers
              </Typography>

              {phoneNumbers.length === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No phone numbers added. These will appear in the channel sidebar for reference.
                </Alert>
              ) : (
                <List disablePadding>
                  {phoneNumbers.map((phone) => (
                    <ListItem
                      key={phone.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        pr: 10,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {phone.label}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption">
                            {phone.phone_number} — {phone.type}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => { setEditingPhone(phone); setPhoneFormOpen(true); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => handleDeletePhone(phone.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => { setEditingPhone(null); setPhoneFormOpen(true); }}
                fullWidth
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Add Phone Number
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Sub-dialogs */}
      <EmailAccountForm
        open={emailFormOpen}
        onClose={() => setEmailFormOpen(false)}
        onSaved={handleAccountSaved}
        editingAccount={editingAccount}
      />
      <PhoneNumberForm
        open={phoneFormOpen}
        onClose={() => setPhoneFormOpen(false)}
        onSaved={handlePhoneSaved}
        editingPhone={editingPhone}
      />
    </>
  );
};

export default AccountManagerDrawer;
